/**
 * Icon Map
 *
 * Maps string icon names from AppConfig to Lucide React components.
 * Uses a bounded set of commonly-used SaaS icons to keep bundle size small.
 */

import React from 'react';
import {
  Users,
  Users2,
  LayoutDashboard,
  BarChart3,
  FileText,
  FileSignature,
  Settings,
  Target,
  ClipboardList,
  Package,
  ShoppingCart,
  CreditCard,
  Bell,
  Search,
  Home,
  Mail,
  Phone,
  Building,
  Building2,
  Globe,
  Calendar,
  Clock,
  Star,
  Heart,
  BookOpen,
  MessageSquare,
  MessageCircle,
  Shield,
  Key,
  Lock,
  Unlock,
  Folder,
  FolderOpen,
  Upload,
  Download,
  Plus,
  Trash2,
  Edit3,
  Eye,
  EyeOff,
  Check,
  CheckSquare,
  X,
  AlertTriangle,
  Info,
  HelpCircle,
  ChevronRight,
  ArrowRight,
  ExternalLink,
  Link,
  Image,
  Video,
  Mic,
  Zap,
  Activity,
  TrendingUp,
  PieChart,
  DollarSign,
  Briefcase,
  UserPlus,
  UserCheck,
  Tag,
  Hash,
  Filter,
  SlidersHorizontal,
  Layers,
  Grid3X3,
  List,
  Map,
  MapPin,
  Inbox,
  Send,
  Archive,
  RefreshCw,
  CircleDot,
  Contact,
  GitBranch,
  Handshake,
  Webhook,
  Ticket,
  Receipt,
  Repeat,
  Flag,
  Megaphone,
  Plug,
  ScrollText,
  BadgePercent,
  Store,
  Truck,
  type LucideIcon,
} from 'lucide-react';

const iconMap: Record<string, LucideIcon> = {
  Users,
  Users2,
  LayoutDashboard,
  BarChart3,
  FileText,
  FileSignature,
  Settings,
  Target,
  ClipboardList,
  Package,
  ShoppingCart,
  CreditCard,
  Bell,
  Search,
  Home,
  Mail,
  Phone,
  Building,
  Building2,
  Globe,
  Calendar,
  Clock,
  Star,
  Heart,
  BookOpen,
  MessageSquare,
  MessageCircle,
  Shield,
  Key,
  Lock,
  Unlock,
  Folder,
  FolderOpen,
  Upload,
  Download,
  Plus,
  Trash2,
  Edit3,
  Eye,
  EyeOff,
  Check,
  CheckSquare,
  X,
  AlertTriangle,
  Info,
  HelpCircle,
  ChevronRight,
  ArrowRight,
  ExternalLink,
  Link,
  Image,
  Video,
  Mic,
  Zap,
  Activity,
  TrendingUp,
  PieChart,
  DollarSign,
  Briefcase,
  UserPlus,
  UserCheck,
  Tag,
  Hash,
  Filter,
  SlidersHorizontal,
  Layers,
  Grid3X3,
  List,
  Map,
  MapPin,
  Inbox,
  Send,
  Archive,
  RefreshCw,
  CircleDot,
  Contact,
  GitBranch,
  Handshake,
  Webhook,
  Ticket,
  Receipt,
  Repeat,
  Flag,
  Megaphone,
  Plug,
  ScrollText,
  BadgePercent,
  Store,
  Truck,
};

/**
 * Set of all valid icon names that the runtime can render.
 * Used by the generator and validator to ensure only resolvable icons are used.
 */
export const VALID_ICON_NAMES: ReadonlySet<string> = new Set(Object.keys(iconMap));

/**
 * Resolve an icon name string to a React element.
 * Returns undefined if the icon name is not found.
 */
export function resolveIcon(name: string, className = 'h-5 w-5'): React.ReactNode | undefined {
  const Icon = iconMap[name];
  if (!Icon) {
    console.warn(`[runtime-engine] Unknown icon: "${name}". Available: ${Object.keys(iconMap).join(', ')}`);
    return undefined;
  }
  return <Icon className={className} />;
}

/**
 * Get the raw Lucide icon component by name.
 */
export function getIconComponent(name: string): LucideIcon | undefined {
  return iconMap[name];
}
